import { useEffect, useState, useCallback } from 'react'
import {
  Shield, Lock, Plus, Pencil, Copy, Trash2, AlertCircle, X,
  Users, Check, Eye, Minus, AlertTriangle,
} from 'lucide-react'
import {
  listRoles,
  listPermissions,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  cloneRole,
  listRoleMembers,
  addRoleMember,
  removeRoleMember,
  type Role,
  type RoleDetail,
  type Permission,
  type RoleMember,
} from '../../lib/api'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { InfoTooltip } from '../../components/ui/InfoTooltip'
import { RolePermissionPicker } from '../../components/RolePermissionPicker'
import { groupCatalog, levelFor, LEVEL_LABELS } from '../../lib/rolePermissions'
import { UserSearchPicker } from '../../components/UserSearchPicker'

interface EditorState {
  mode: 'create' | 'edit' | 'clone'
  roleId?: number
  isSystem: boolean
  name: string
  description: string
  permissionKeys: string[]
}

type Tab = 'permissions' | 'members'

export default function Roles() {
  const [roles, setRoles] = useState<Role[]>([])
  const [catalog, setCatalog] = useState<Permission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<RoleDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [tab, setTab] = useState<Tab>('permissions')
  const [members, setMembers] = useState<Record<number, RoleMember[]>>({})

  const [editor, setEditor] = useState<EditorState | null>(null)
  const [saving, setSaving] = useState(false)

  const flashSuccess = useCallback((msg: string) => {
    setSuccess(msg)
    window.setTimeout(() => setSuccess(null), 3000)
  }, [])

  const load = useCallback(async () => {
    try {
      const [rolesResp, permsResp] = await Promise.all([listRoles(), listPermissions()])
      setRoles(rolesResp.roles)
      setCatalog(permsResp.permissions)
      return rolesResp.roles
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load roles')
      throw e
    } finally {
      setLoading(false)
    }
  }, [])

  const loadDetail = useCallback(async (roleId: number) => {
    setDetailLoading(true)
    try {
      const d = await getRole(roleId)
      setDetail(d)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load role')
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const loadMembers = useCallback(async (roleId: number) => {
    try {
      const resp = await listRoleMembers(roleId)
      setMembers((prev) => ({ ...prev, [roleId]: resp.members }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load members')
    }
  }, [])

  function selectRole(role: Role) {
    setEditor(null)
    setError(null)
    setTab('permissions')
    setSelectedId(role.id)
    loadDetail(role.id)
  }

  useEffect(() => {
    load().catch(() => {})
  }, [load])

  // Auto-select the first role once loaded so the detail pane is never empty.
  useEffect(() => {
    if (selectedId == null && editor == null && roles.length > 0) {
      setSelectedId(roles[0].id)
      loadDetail(roles[0].id)
    }
  }, [roles, selectedId, editor, loadDetail])

  function openTab(next: Tab) {
    setTab(next)
    if (next === 'members' && selectedId != null && !members[selectedId]) {
      loadMembers(selectedId)
    }
  }

  async function handleAddMember(roleId: number, userId: number) {
    setError(null)
    try {
      await addRoleMember(roleId, userId)
      await Promise.all([loadMembers(roleId), load()])
      flashSuccess('Member added')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add member')
    }
  }

  async function handleRemoveMember(roleId: number, userId: number) {
    setError(null)
    try {
      await removeRoleMember(roleId, userId)
      await Promise.all([loadMembers(roleId), load()])
      flashSuccess('Member removed')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove member')
    }
  }

  function openCreate() {
    setError(null)
    setEditor({ mode: 'create', isSystem: false, name: '', description: '', permissionKeys: [] })
  }

  async function openEdit(role: Role) {
    setError(null)
    try {
      const d = await getRole(role.id)
      setEditor({
        mode: 'edit',
        roleId: role.id,
        isSystem: d.isSystem,
        name: d.name,
        description: d.description,
        permissionKeys: d.permissionKeys,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load role')
    }
  }

  async function openClone(role: Role) {
    setError(null)
    try {
      const d = await getRole(role.id)
      setEditor({
        mode: 'clone',
        roleId: role.id,
        isSystem: false,
        name: `${d.name} (copy)`,
        description: d.description,
        permissionKeys: d.permissionKeys,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load role')
    }
  }

  async function handleSave() {
    if (!editor) return
    setSaving(true)
    setError(null)
    try {
      let savedId: number | undefined
      if (editor.mode === 'edit' && editor.roleId != null) {
        const r = await updateRole(editor.roleId, {
          name: editor.name,
          description: editor.description,
          permissionKeys: editor.permissionKeys,
        })
        savedId = r.id
        flashSuccess('Role updated')
      } else if (editor.mode === 'clone' && editor.roleId != null) {
        const r = await cloneRole(editor.roleId, { name: editor.name, description: editor.description })
        savedId = r.id
        flashSuccess('Role cloned')
      } else {
        const r = await createRole({
          name: editor.name,
          description: editor.description,
          permissionKeys: editor.permissionKeys,
        })
        savedId = r.id
        flashSuccess('Role created')
      }
      setEditor(null)
      await load()
      if (savedId != null) {
        setSelectedId(savedId)
        setTab('permissions')
        await loadDetail(savedId)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save role')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(role: Role) {
    setError(null)
    try {
      await deleteRole(role.id)
      flashSuccess('Role deleted')
      const remaining = await load()
      const next = remaining.find((r) => r.id !== role.id) ?? null
      if (next) {
        setSelectedId(next.id)
        await loadDetail(next.id)
      } else {
        setSelectedId(null)
        setDetail(null)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete role')
    }
  }

  const systemRoles = roles.filter((r) => r.isSystem)
  const customRoles = roles.filter((r) => !r.isSystem)
  const selectedRole = roles.find((r) => r.id === selectedId) ?? null
  const groups = groupCatalog(catalog)

  function RoleListItem({ role }: { role: Role }) {
    const active = role.id === selectedId && editor == null
    return (
      <button
        onClick={() => selectRole(role)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left border-l-2 transition-colors ${
          active
            ? 'border-primary bg-muted'
            : 'border-transparent hover:bg-muted/50'
        }`}
      >
        <span
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
            role.isSystem ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          }`}
        >
          {role.isSystem ? <Shield className="w-4 h-4" /> : <Users className="w-4 h-4" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-medium truncate">{role.name}</span>
          <span className="block text-xs text-muted-foreground">
            {role.userCount ?? 0} {(role.userCount ?? 0) === 1 ? 'member' : 'members'}
          </span>
        </span>
      </button>
    )
  }

  const sectionHeader = 'px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" /> Roles
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Select a role to view its permissions and members
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" /> New role
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 rounded-lg bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">
          {success}
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading roles…</p>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          {/* LEFT: role list */}
          <div className="lg:col-span-4">
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {systemRoles.length > 0 && (
                <>
                  <div className={`${sectionHeader} border-b border-border`}>System</div>
                  {systemRoles.map((role) => (
                    <RoleListItem key={role.id} role={role} />
                  ))}
                </>
              )}
              <div className={`${sectionHeader} border-y border-border`}>Custom</div>
              {customRoles.length > 0 ? (
                customRoles.map((role) => <RoleListItem key={role.id} role={role} />)
              ) : (
                <p className="px-4 py-3 text-sm text-muted-foreground">No custom roles yet.</p>
              )}
            </div>
          </div>

          {/* RIGHT: detail / editor */}
          <div className="lg:col-span-8">
            {editor ? (
              <RoleEditor
                editor={editor}
                catalog={catalog}
                saving={saving}
                onChange={setEditor}
                onCancel={() => setEditor(null)}
                onSave={handleSave}
              />
            ) : selectedRole ? (
              <div className="rounded-xl border border-border bg-card">
                {/* detail header */}
                <div className="flex flex-col gap-4 px-6 py-5 border-b border-border sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${
                        selectedRole.isSystem
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {selectedRole.isSystem ? <Shield className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-semibold truncate">{selectedRole.name}</h2>
                        {selectedRole.isSystem && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                            <Lock className="w-3 h-3" /> System
                          </span>
                        )}
                      </div>
                      {selectedRole.description && (
                        <p className="text-sm text-muted-foreground">{selectedRole.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                    {selectedRole.isSystem ? (
                      <span className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-muted text-muted-foreground">
                        <Lock className="w-3.5 h-3.5" /> Read-only
                      </span>
                    ) : (
                      <>
                        <button
                          onClick={() => openEdit(selectedRole)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button
                          onClick={() => openClone(selectedRole)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5" /> Clone
                        </button>
                        <ConfirmDialog
                          trigger={
                            <button className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          }
                          title={`Delete role "${selectedRole.name}"?`}
                          description={
                            (selectedRole.userCount ?? 0) > 0
                              ? `This role is assigned to ${selectedRole.userCount} user(s). You must unassign it first or the delete will be rejected.`
                              : 'This permanently removes the role.'
                          }
                          confirmLabel="Delete"
                          variant="destructive"
                          onConfirm={() => handleDelete(selectedRole)}
                        />
                      </>
                    )}
                  </div>
                </div>

                {/* tabs */}
                <div className="flex gap-6 px-6 pt-3 border-b border-border text-sm">
                  <button
                    onClick={() => openTab('permissions')}
                    className={`pb-3 -mb-px border-b-2 transition-colors ${
                      tab === 'permissions'
                        ? 'border-primary font-medium text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Permissions
                  </button>
                  <button
                    onClick={() => openTab('members')}
                    className={`pb-3 -mb-px border-b-2 transition-colors ${
                      tab === 'members'
                        ? 'border-primary font-medium text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Members <span className="text-muted-foreground">{selectedRole.userCount ?? 0}</span>
                  </button>
                </div>

                {/* tab content */}
                {tab === 'permissions' ? (
                  detailLoading ? (
                    <p className="p-6 text-sm text-muted-foreground">Loading permissions…</p>
                  ) : (
                    <div className="p-6 grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
                      {groups.map((group) => {
                        const level = levelFor(group, detail?.permissionKeys ?? [])
                        return (
                          <div
                            key={group.category}
                            className="flex items-center justify-between gap-2 py-2 border-b border-border/50"
                          >
                            <span className="flex items-center gap-1.5 text-sm">
                              {group.category}
                              {group.sensitive && (
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                              )}
                              <InfoTooltip
                                label={`What does ${group.category} grant?`}
                                content={
                                  <div className="space-y-1.5">
                                    {group.sensitive && (
                                      <p className="flex items-center gap-1 font-medium text-amber-600">
                                        <AlertTriangle className="w-3 h-3" /> Sensitive permission
                                      </p>
                                    )}
                                    {group.readDescription && (
                                      <p>
                                        <span className="font-medium">Read:</span> {group.readDescription}
                                      </p>
                                    )}
                                    {group.writeDescription && (
                                      <p>
                                        <span className="font-medium">Read &amp; write:</span> {group.writeDescription}
                                      </p>
                                    )}
                                  </div>
                                }
                              />
                            </span>
                            <PermissionLevelBadge level={level} />
                          </div>
                        )
                      })}
                    </div>
                  )
                ) : (
                  <div className="p-6">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <h4 className="text-sm font-medium flex items-center gap-1">
                        <Users className="w-4 h-4" /> Members
                      </h4>
                      <UserSearchPicker
                        excludeIds={(members[selectedRole.id] ?? []).map((m) => m.id)}
                        onPick={(uid) => handleAddMember(selectedRole.id, uid)}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(members[selectedRole.id] ?? []).map((m) => (
                        <span
                          key={m.id}
                          className="flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-sm border border-border bg-muted/40"
                        >
                          {m.email}
                          <button
                            type="button"
                            aria-label={`Remove ${m.email}`}
                            onClick={() => handleRemoveMember(selectedRole.id, m.id)}
                            className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      {(members[selectedRole.id] ?? []).length === 0 && (
                        <p className="text-sm text-muted-foreground">No members yet.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground">
                Select a role to view its details, or create a new one.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function PermissionLevelBadge({ level }: { level: 'none' | 'read' | 'readwrite' }) {
  if (level === 'none') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Minus className="w-3.5 h-3.5" /> {LEVEL_LABELS.none}
      </span>
    )
  }
  if (level === 'read') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Eye className="w-3.5 h-3.5" /> {LEVEL_LABELS.read}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
      <Check className="w-3.5 h-3.5" /> {LEVEL_LABELS.readwrite}
    </span>
  )
}

interface RoleEditorProps {
  editor: EditorState
  catalog: Permission[]
  saving: boolean
  onChange: (next: EditorState) => void
  onCancel: () => void
  onSave: () => void
}

function RoleEditor({ editor, catalog, saving, onChange, onCancel, onSave }: RoleEditorProps) {
  const title =
    editor.mode === 'edit'
      ? editor.isSystem
        ? 'View role'
        : 'Edit role'
      : editor.mode === 'clone'
        ? 'Clone role'
        : 'Create role'

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between px-6 py-5 border-b border-border">
        <h2 className="text-lg font-semibold">{title}</h2>
        <button
          onClick={onCancel}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Cancel"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Name</label>
          <input
            value={editor.name}
            disabled={editor.isSystem}
            onChange={(e) => onChange({ ...editor, name: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background disabled:opacity-60"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <input
            value={editor.description}
            disabled={editor.isSystem}
            onChange={(e) => onChange({ ...editor, description: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background disabled:opacity-60"
          />
        </div>
        {editor.mode !== 'clone' && (
          <div>
            <label className="block text-sm font-medium mb-2">Permissions</label>
            <RolePermissionPicker
              catalog={catalog}
              value={editor.permissionKeys}
              onChange={(keys) => onChange({ ...editor, permissionKeys: keys })}
              readOnly={editor.isSystem}
            />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
          >
            Cancel
          </button>
          {!editor.isSystem && (
            <button
              onClick={onSave}
              disabled={saving || !editor.name.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
